import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import { getEffectiveAgentInfo } from '../../utils/agentIdentity';
import Select from 'react-select';
import StateCard from '../../components/StateCard';
import { groupCarriersByName } from '../../utils/carrierHelpers';
import { isFluxDomain } from '../../utils/fluxTheme';
import './AgentOnboarding.css';

// Helper: inject agent data into consent — replaces empty placeholder lines.
// Works whether the consent is freshly generated or already saved in DB.
function injectAgentDataIntoConsent(consent, name = '', npn = '', email = '') {
  if (!consent) return buildDefaultConsent(name, npn, email);
  let updated = consent;
  // Only replace if the value after ":" is blank/whitespace (don't overwrite real values)
  updated = updated.replace(/^(- Agent Name:)\s*$/gm, `$1 ${name}`);
  updated = updated.replace(/^(- NPN:)\s*$/gm,        `$1 ${npn}`);
  updated = updated.replace(/^(- Email:)\s*$/gm,       `$1 ${email}`);
  return updated;
}

// Helper: build default consent pre-filled with agent's own data
function buildDefaultConsent(name = '', npn = '', email = '') {
  return `# Health Insurance Consent and Acknowledgment Form

**Client Authorization Statement:**

I, the undersigned client, hereby authorize the health insurance agents listed below to access my information through the Health Insurance Marketplace and to contact me for purposes related to enrollment and servicing of coverage.

This authorization applies only to the 2026 coverage year and will expire on December 31, 2026, unless revoked earlier by me. A new authorization is required for any future coverage year.

**Authorized Agent:**
- Agent Name: ${name}
- NPN: ${npn}
- Email: ${email}

**Scope of Authorization**

I grant permission to the above named agents to act as my health insurance agents or brokers for myself and, if applicable, my household. This authorization applies to enrollment in a Qualified Health Plan offered on the Federally Facilitated Marketplace.

By consenting to this authorization, I allow the agents to view and use my confidential information provided in writing, electronically, or by telephone for the following purposes:

- Searching for an existing Marketplace application
- Completing an application for eligibility and enrollment in a Marketplace Qualified Health Plan or other government insurance affordability programs such as Medicaid, CHIP, or Advance Premium Tax Credits
- Providing account maintenance and ongoing enrollment assistance as needed
- Responding to Marketplace inquiries about my application

**Marketplace Account Access Authorization**

By signing this form, I authorize the above named agent(s) to access, view, update, and submit my application through the Federally Facilitated Marketplace, HealthCare.gov. This includes access to income, household, tax, immigration, and other eligibility related data necessary to complete enrollment. I understand that by signing this document, I am authorizing my assigned agents and their authorized technology partners to securely submit my application for said health insurance to the Marketplace and approved Web Brokers using automated or manual processing systems.

**Compensation Disclosure**

The authorized agent(s) may receive compensation from health insurance carriers if I enroll in a plan. There is no additional cost to me for using the services of a licensed agent.

**Agent of Record Rights**

I understand that I may remove or change my Agent of Record at any time through my HealthCare.gov account or by contacting the Marketplace Call Center.

**Privacy and Use of Personal Information**

I understand that my Personally Identifiable Information will be used solely for the purposes listed above. The authorized agents will safeguard my information and take necessary measures to maintain privacy and security.

My information will be handled in accordance with Marketplace privacy and security standards under 45 CFR §155.260 and applicable federal privacy laws.

**Affirmation of Information Accuracy**

I attest that my projected annual household income listed in this application is true, complete, and accurate to the best of my knowledge. I understand that providing inaccurate income information may result in repayment of Advance Premium Tax Credits or other financial consequences.

**Revocation of Consent and Plan Cancellation**

I understand that I may revoke this consent or request cancellation of my health insurance plan at any time by contacting the authorized agents or agency by phone at +1 863-316-4690, by email, by written notice, or by removing the agent's access through my HealthCare.gov Marketplace account.

Revocation will not affect actions taken prior to the date of revocation.

**Primary Writing Agents and Agency Information**
- Agent Name: ${name}
- NPN: ${npn}
- Email: ${email}`;
}

function AgentOnboarding({ token }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  
  // Step 1: Video
  const [videoWatched, setVideoWatched] = useState(false);
  
  // Step 2: PDF
  const [pdfViewed, setPdfViewed] = useState(false);
  
  // Step 3: Config editor
  const [formData, setFormData] = useState({
    statesWithCarriers: {},
    consent: buildDefaultConsent(), // Will be replaced with agent data once profile loads
    healthSherpaMarketingLink: '',
    dentalInsuranceLink: '',
    lifeInsuranceLink: '',
    autoInsuranceLink: '',
    renewableConsent: ''
  });
  const [states, setStates] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [editingState, setEditingState] = useState(null);
  const [showAddState, setShowAddState] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [newStateCarriers, setNewStateCarriers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
   // Step 4: Survey
   const [surveyCompleted, setSurveyCompleted] = useState(false);
   const [verifying, setVerifying] = useState(false);
   const [verificationError, setVerificationError] = useState('');
   const [showVerificationError, setShowVerificationError] = useState(false);
   
   // Step 5: Additional embedded form
   const [step5Completed, setStep5Completed] = useState(false);

  useEffect(() => {
    fetchOnboardingStatus();
    fetchStates();
    fetchCarriers();
  }, []);

  // Auto-dismiss verification error after 10 seconds
  useEffect(() => {
    if (verificationError && showVerificationError) {
      const timer = setTimeout(() => {
        setShowVerificationError(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [verificationError, showVerificationError]);

  const fetchOnboardingStatus = async () => {
    try {
      const response = await axios.get('/api/agent/onboarding-status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { currentStep: savedStep, profile: agentProfile, stepsCompleted } = response.data;
      
      // ⭐ GHL gate: step4 flag is set ONLY by verify-survey-completion endpoint (GHL contact found)
      // This is the confirmation that the agent actually submitted the order form in GHL.
      const ghlOrderVerified = stepsCompleted?.step4 === true;

      // ⭐ FIX 1: Only redirect to dashboard if BOTH onboarding complete AND GHL order form verified
      if ((agentProfile.onboardingCompleted || agentProfile.onboarding?.completed) && ghlOrderVerified) {
        console.log('[ONBOARDING] Onboarding complete + GHL order verified, redirecting to dashboard...');
        window.location.replace('/agent');
        return;
      }

      // ⭐ FIX 2: Auto-complete only when step1+step3 AND GHL order form verified
      const activeStepsComplete = (stepsCompleted?.step1 || stepsCompleted?.step2) && stepsCompleted?.step3;
      if (activeStepsComplete && ghlOrderVerified) {
        console.log('[ONBOARDING] All steps + GHL verified — auto-finishing onboarding...');
        try {
          await axios.post('/api/agent/complete-onboarding', {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('[ONBOARDING] ✅ Auto-completed onboarding, redirecting to dashboard...');
        } catch (completeError) {
          console.error('[ONBOARDING] Auto-complete error (ignoring):', completeError.message);
        }
        window.location.replace('/agent');
        return;
      }

      // ⭐ FIX 3: If onboarding marked complete OR step1+step3 done but GHL NOT yet verified,
      // keep agent on step 5 so they must submit & verify the order form before proceeding.
      const shouldStayOnStep5 = (agentProfile.onboardingCompleted || agentProfile.onboarding?.completed)
        || activeStepsComplete
        || (savedStep && savedStep >= 5);
      
      setProfile(agentProfile);
      // ⭐ FIX 4: If should stay on step 5 (GHL not verified), force step 5.
      // Otherwise clamp to 1 or 3. Step 2 is merged into 1, old savedStep 2 → restart from 1.
      const effectiveStep = shouldStayOnStep5 ? 5 : ((!savedStep || savedStep < 1 || savedStep === 2) ? 1 : Math.min(savedStep, 3));
      setCurrentStep(effectiveStep);
      
      // Restore step completion status
      if (stepsCompleted) {
        setVideoWatched(stepsCompleted.step1 || false);
        setPdfViewed(stepsCompleted.step2 || false);
        setSurveyCompleted(stepsCompleted.step4 || false);
      }
      
      // Load existing config.
      // injectAgentDataIntoConsent handles all three cases:
      //   1. No saved consent → buildDefaultConsent with real data
      //   2. Saved consent with empty placeholder lines → fills them in
      //   3. Saved consent already has real values → leaves them untouched
      // Use member-aware identity (handles group member logins)
      const { name: effectiveName, email: effectiveEmail } = getEffectiveAgentInfo(agentProfile);
      const name  = effectiveName  || agentProfile.name  || '';
      const npn   = agentProfile.npn   || '';
      const email = effectiveEmail || agentProfile.email || '';

      setFormData(prev => ({
        ...prev,
        statesWithCarriers: agentProfile.statesWithCarriers || prev.statesWithCarriers,
        consent: injectAgentDataIntoConsent(agentProfile.consent, name, npn, email),
        healthSherpaMarketingLink: agentProfile.healthSherpaMarketingLink || prev.healthSherpaMarketingLink,
        dentalInsuranceLink: agentProfile.dentalInsuranceLink || prev.dentalInsuranceLink,
        lifeInsuranceLink: agentProfile.lifeInsuranceLink || prev.lifeInsuranceLink,
        autoInsuranceLink: agentProfile.autoInsuranceLink || prev.autoInsuranceLink,
        renewableConsent: agentProfile.renewableConsent || prev.renewableConsent
      }));
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching onboarding status:', error);
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await axios.get('/api/agent/states', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStates(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchCarriers = async () => {
    try {
      const response = await axios.get('/api/agent/carriers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarriers(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const saveOnboardingProgress = async (step, stepCompleted = false) => {
    try {
      await axios.post('/api/agent/onboarding-progress', {
        currentStep: step,
        stepCompleted: stepCompleted ? step : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const handleNextStep = async () => {
    // Save progress before moving to next step
    await saveOnboardingProgress(currentStep, true);
    
    // Step 1 (merged platform guide) → Step 3 (config). Step 2 is merged into step 1.
    if (currentStep === 1) {
      setCurrentStep(3);
      await saveOnboardingProgress(3);
    } else if (currentStep === 3) {
      // Step 4 temporarily disabled — skip directly to step 5
      setCurrentStep(5);
      await saveOnboardingProgress(5);
    } else if (currentStep === 5) {
      await handleFinishOnboarding();
    }
  };

  const handlePrevStep = async () => {
    // Step 3 goes back to Step 1 (step 2 is merged into step 1)
    if (currentStep === 3) {
      setCurrentStep(1);
      await saveOnboardingProgress(1);
    } else if (currentStep === 5) {
      // Step 4 disabled — prev goes back to step 3
      setCurrentStep(3);
      await saveOnboardingProgress(3);
    }
  };

  const handleVideoEnd = () => {
    setVideoWatched(true);
  };

  const handlePdfView = () => {
    // Mark PDF as viewed after 10 seconds
    setTimeout(() => {
      setPdfViewed(true);
    }, 10000);
  };

  // Config editor functions
  const updateStateCarriers = (state, carriers) => {
    setFormData(prev => ({
      ...prev,
      statesWithCarriers: {
        ...prev.statesWithCarriers,
        [state]: carriers
      }
    }));
  };

  const deleteState = (state) => {
    const newStates = { ...formData.statesWithCarriers };
    delete newStates[state];
    setFormData(prev => ({ ...prev, statesWithCarriers: newStates }));
  };

  const addNewState = () => {
    if (selectedState && newStateCarriers.length > 0) {
      updateStateCarriers(selectedState.value, newStateCarriers);
      setSelectedState(null);
      setNewStateCarriers([]);
      setShowAddState(false);
    }
  };

  const handleConfigSubmit = async (e) => {
    e.preventDefault();
    
    if (Object.keys(formData.statesWithCarriers).length === 0) {
      toast.error('Please add at least one state with carriers');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Direct update (not change request) during onboarding
      await axios.post('/api/agent/onboarding-config', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Configuration saved successfully!');
      await handleNextStep();
    } catch (error) {
      toast.error('Error saving configuration');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const checkSurveyVerification = async () => {
    if (verifying) return; // Prevent duplicate calls
    
    setVerifying(true);
    setVerificationError('');
    
    try {
      const response = await axios.post('/api/agent/verify-survey-completion', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.verified) {
        setSurveyCompleted(true);
        // Move to Step 5 instead of redirecting to dashboard
        await handleNextStep();
      } else {
        const errorMsg = response.data.message || 'Registration form not found. Please complete the onboarding form before proceeding.';
        setVerificationError(errorMsg);
        setShowVerificationError(true);
      }
    } catch (error) {
      console.error('Verification error:', error);
      // Properly extract error message - handle both string and object responses
      let errorMsg = 'Error verifying survey completion';
      
      if (error.response?.data) {
        const data = error.response.data;
        
        // Try different possible error message locations
        if (typeof data === 'string') {
          errorMsg = data;
        } else if (data.message) {
          errorMsg = data.message;
        } else if (data.error) {
          // If error is a string, use it; if it's an object, use its message property
          errorMsg = typeof data.error === 'string' ? data.error : (data.error.message || 'Error verifying survey completion');
        } else if (data.details) {
          errorMsg = typeof data.details === 'string' ? data.details : JSON.stringify(data.details);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      setVerificationError(errorMsg);
      setShowVerificationError(true);
    } finally {
      setVerifying(false);
    }
  };

  const handleSurveyComplete = async () => {
    // Instead of just marking complete, verify with GHL
    await checkSurveyVerification();
  };

  const handleFinishOnboarding = async () => {
    try {
      await axios.post('/api/agent/complete-onboarding', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Onboarding completed! Redirecting to dashboard...');
      
      // Force full page reload to refresh App.js onboarding status check
      setTimeout(() => {
        window.location.href = '/agent';
      }, 1500);
    } catch (error) {
      toast.error('Error completing onboarding');
      console.error(error);
    }
  };

  if (loading) {
    return <div className="loading loading-onboarding">Loading onboarding...</div>;
  }

  const steps = [
    { number: 1, title: 'Platform Guide', icon: '📄' },
    { number: 3, title: 'Setup Configuration', icon: '⚙️' },
    // { number: 4, title: 'Complete Registration', icon: '✅' },  // TEMPORARILY DISABLED
    { number: 5, title: 'Order Opportunities', icon: '🎯' }
  ];

  const groupedAllCarriers = groupCarriersByName(carriers);
  const stateOptions = states
    .filter(s => !formData.statesWithCarriers[s.code])
    .map(s => ({ value: s.code, label: `${s.name} (${s.code})` }));
  
  const _oSelCode = selectedState?.value || '';
  const _oScMap = {AL:'alabama',AK:'alaska',AZ:'arizona',AR:'arkansas',CA:'california',CO:'colorado',CT:'connecticut',DE:'delaware',FL:'florida',GA:'georgia',HI:'hawaii',ID:'idaho',IL:'illinois',IN:'indiana',IA:'iowa',KS:'kansas',KY:'kentucky',LA:'louisiana',ME:'maine',MD:'maryland',MA:'massachusetts',MI:'michigan',MN:'minnesota',MS:'mississippi',MO:'missouri',MT:'montana',NE:'nebraska',NV:'nevada',NH:'new hampshire',NJ:'new jersey',NM:'new mexico',NY:'new york',NC:'north carolina',ND:'north dakota',OH:'ohio',OK:'oklahoma',OR:'oregon',PA:'pennsylvania',RI:'rhode island',SC:'south carolina',SD:'south dakota',TN:'tennessee',TX:'texas',UT:'utah',VT:'vermont',VA:'virginia',WA:'washington',WV:'west virginia',WI:'wisconsin',WY:'wyoming'};
  const _oAllSN = Object.values(_oScMap);
  const _oCurSN = _oScMap[_oSelCode] || '';
  const carrierOptions = groupedAllCarriers
    .filter(group => {
      if (_oSelCode && group.name) {
        const n = group.name.toLowerCase();
        for (const sn of _oAllSN) { if (sn !== _oCurSN && n.includes(sn)) return false; }
      }
      const allSelected = group.carrierIds.every(carrierId =>
        newStateCarriers.some(nc => nc.CarrierId === carrierId)
      );
      return !allSelected;
    })
    .map(group => ({
      value: group,
      label: group.name
    }));

  return (
    <div className="onboarding-container">
      <div className="onboarding-header">
        <h1>Welcome to Flux Protocol! 🎉</h1>
        <p>Let's get you set up in just 2 simple steps</p>
      </div>

      {/* Stepper */}
      <div className="stepper">
        {steps.map((step) => (
          <div
            key={step.number}
            className={`step ${currentStep === step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}
          >
            <div className="step-number">
              {currentStep > step.number ? '✓' : step.icon}
            </div>
            <div className="step-title">{step.title}</div>
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="step-content">
        {/* Step 1: Full-page HTML guide (video removed) */}
        {currentStep === 1 && (
          <div className="step-panel" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
            <iframe
              src="/Flux_Step1_FINAL_ABSOLUTELY_NO_BUTTON.html"
              title="Platform Guide"
              width="100%"
              style={{
                flex: 1,
                border: 'none',
                minHeight: 'calc(100vh - 220px)',
                display: 'block'
              }}
              onLoad={handlePdfView}
            />
            <div className="step-actions" style={{ padding: '16px 24px' }}>
              <button
                className="btn btn-primary"
                onClick={handleNextStep}
              >
                Next Step →
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Config Editor */}
        {currentStep === 3 && (
          <div className="step-panel">
            <h2>Step 3: Setup Your Configuration</h2>
            <p>Add the states and carriers you work with, plus your consent text. You must add at least 1 state with its approved carriers before you can proceed.</p>
            
            <form onSubmit={handleConfigSubmit}>
              <div className="form-group">
                <div className="states-header">
                  <label>States & Carriers</label>
                  <button 
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => setShowAddState(!showAddState)}
                  >
                    {showAddState ? '✕ Cancel' : '+ Add State'}
                  </button>
                </div>

                {showAddState && (
                  <div className="add-state-form">
                    <Select
                      options={stateOptions}
                      value={selectedState}
                      onChange={setSelectedState}
                      placeholder="Select a state..."
                      className="add-state-select"
                    />
                    
                    {selectedState && (
                      <div style={{ marginTop: '12px' }}>
                        <Select
                          isMulti
                          options={carrierOptions}
                          value={groupCarriersByName(newStateCarriers).map(group => ({
                            value: group,
                            label: group.name
                          }))}
                          onChange={(selected) => {
                            if (!selected || selected.length === 0) {
                              setNewStateCarriers([]);
                            } else {
                              const newCarriers = [];
                              selected.forEach(item => {
                                item.value.carrierIds.forEach(carrierId => {
                                  const carrier = carriers.find(c => c.CarrierId === carrierId);
                                  if (carrier) {
                                    newCarriers.push(carrier);
                                  }
                                });
                              });
                              setNewStateCarriers(newCarriers);
                            }
                          }}
                          placeholder="Search and select carriers..."
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          onClick={addNewState}
                          disabled={!selectedState || newStateCarriers.length === 0}
                          style={{ marginTop: '8px' }}
                        >
                          ✓ Add {selectedState.value} with {newStateCarriers.length} carrier{newStateCarriers.length !== 1 ? 's' : ''}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="states-list" style={{marginTop: '10px'}}>
                  {Object.keys(formData.statesWithCarriers).length === 0 ? (
                    <div className="empty-state-message">
                      No states added yet. Click "+ Add State" to begin.
                    </div>
                  ) : (
                    Object.entries(formData.statesWithCarriers).map(([state, stateCarriers]) => (
                      <StateCard
                        key={state}
                        stateCode={state}
                        carriers={stateCarriers}
                        allCarriers={carriers}
                        onUpdate={updateStateCarriers}
                        onDelete={deleteState}
                        isEditing={editingState === state}
                        setIsEditing={(editing) => setEditingState(editing ? state : null)}
                        isAdminView={false}
                      />
                    ))
                  )}
                </div>
              </div>
{/* 
              {!isFluxDomain() && ( */}
                <div className="form-group" style={{marginTop: '24px'}}>
                  <label>🔗 HealthSherpa Marketing Link</label>
                  <input
                    type="url"
                    value={formData.healthSherpaMarketingLink}
                    onChange={(e) => setFormData({ ...formData, healthSherpaMarketingLink: e.target.value })}
                    placeholder="https://example.healthsherpa.com/agent123"
                    className="form-control"
                  />
                </div>

                <div style={{ marginTop: 20, padding: '14px 16px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0369a1', marginBottom: 12 }}>📋 Optional Insurance Links (shown to your clients)</div>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label>🦷 Dental Insurance Link (Optional)</label>
                    <input
                      type="url"
                      value={formData.dentalInsuranceLink || ''}
                      onChange={(e) => setFormData({ ...formData, dentalInsuranceLink: e.target.value })}
                      placeholder="https://dental-signup-link.com"
                      className="form-control"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label>❤️ Life Insurance Link (Optional)</label>
                    <input
                      type="url"
                      value={formData.lifeInsuranceLink || ''}
                      onChange={(e) => setFormData({ ...formData, lifeInsuranceLink: e.target.value })}
                      placeholder="https://life-insurance-link.com"
                      className="form-control"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>🚗 Auto Insurance Link (Optional)</label>
                    <input
                      type="url"
                      value={formData.autoInsuranceLink || ''}
                      onChange={(e) => setFormData({ ...formData, autoInsuranceLink: e.target.value })}
                      placeholder="https://auto-insurance-link.com"
                      className="form-control"
                    />
                  </div>
                </div>
              {/* )} */}

              <div className="form-group" style={{marginTop: '24px'}}>
                <label>Consent (Markdown)</label>
                <MDEditor
                  value={formData.consent}
                  onChange={(value) => setFormData({ ...formData, consent: value || '' })}
                  height={300}
                />
              </div>

              <div className="form-group" style={{marginTop: '24px'}}>
                <label>Renewable Consent (Markdown) <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>— Shown to returning leads eligible for re-enrollment</span></label>
                <MDEditor
                  value={formData.renewableConsent || ''}
                  onChange={(value) => setFormData({ ...formData, renewableConsent: value || '' })}
                  height={200}
                />
              </div>

              <div className="step-actions">
                <button type="button" className="btn btn-secondary" onClick={handlePrevStep}>
                  ← Previous
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={submitting || Object.keys(formData.statesWithCarriers).length === 0}
                >
                  {submitting ? 'Saving...' : 'Save & Continue →'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 4: Complete Registration */}
        {currentStep === 4 && (
          <div className="step-panel step-panel-survey">
            <div className="survey-header-content">
              <h2>Step 4: Complete Your Registration</h2>
              <p>Fill out the onboarding form below using your registered email: <strong>{profile?.email}</strong></p>
              <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                After submitting the form, click the "I've Completed the Form" button to verify and complete your onboarding.
              </p>
              
              {verificationError && showVerificationError && (
                <div className="alert alert-danger" style={{ 
                  backgroundColor: '#b96363', 
                  border: '1px solid #fcc', 
                  borderRadius: '8px', 
                  padding: '16px', 
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  position: 'relative'
                }}>
                  <span style={{ fontSize: '24px', flexShrink: 0 }}>⚠️</span>
                  <div style={{ flex: 1 }}>
                    <strong>Registration Not Complete</strong>
                    <p style={{ margin: '4px 0 0 0' }}>{verificationError}</p>
                    <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                      Please use your registered email (<strong>{profile?.email}</strong>) when filling out the onboarding form below.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowVerificationError(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#fff',
                      fontSize: '24px',
                      cursor: 'pointer',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      flexShrink: 0,
                      lineHeight: '1'
                    }}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
            
            <div className="survey-container">
              <iframe
                src={(() => {
                  // Build survey URL with pre-filled data
                  const baseUrl = 'https://hello.fluxprotocol.ai/widget/survey/HfSXPGHmTqJwyFDnU5vJ';
                  const params = new URLSearchParams();
                  
                  // Add basic profile fields — use member-scoped identity for group members
                  const { email: effEmail } = getEffectiveAgentInfo(profile);
                  const effMember = profile?._isMemberLogin
                    ? (profile.groupMembers || []).find(m => m.email === profile._loggedInMemberEmail)
                    : null;
                  const effFirst = effMember?.firstName || effMember?.name?.split(' ')[0] || profile?.firstName;
                  const effLast  = effMember?.lastName  || effMember?.name?.split(' ').slice(1).join(' ') || profile?.lastName;
                  const effPhone = effMember?.phone || profile?.phone;
                  const effNpn   = effMember?.npn   || profile?.npn;
                  if (effFirst) params.append('firstName', effFirst);
                  if (effLast)  params.append('lastName',  effLast);
                  if (effEmail) params.append('email', effEmail);
                  if (effPhone) params.append('phone', effPhone);
                  if (effNpn)   params.append('npn', effNpn);
                  
                  // Add states (comma-separated)
                  if (profile?.statesWithCarriers && Object.keys(profile.statesWithCarriers).length > 0) {
                    const states = Object.keys(profile.statesWithCarriers).join(',');
                    params.append('states', states);
                    
                    // Add carriers per state
                    Object.entries(profile.statesWithCarriers).forEach(([state, carriers]) => {
                      if (carriers && Array.isArray(carriers) && carriers.length > 0) {
                        // Get carrier names - try multiple possible property names
                        const carrierNamesArray = carriers
                          .map(c => {
                            // Try different possible property names
                            return c.CarrierName || c.name || c.carrierName || c.Name || 
                                   (typeof c === 'string' ? c : null);
                          })
                          .filter(Boolean);
                        
                        // Remove duplicates using Set
                        const uniqueCarrierNames = [...new Set(carrierNamesArray)].join(',');
                        
                        if (uniqueCarrierNames) {
                          params.append(`carriers_${state}`, uniqueCarrierNames);
                        }
                      }
                    });
                  }
                  
                  const queryString = params.toString();
                  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
                })()}
                title="Registration Survey"
                width="100%"
                height="700px"
                style={{ border: 'none', borderRadius: '8px' }}
                allow="fullscreen"
              />
            </div>

            <div className="survey-footer-content">
              <div className="step-actions" style={{ marginTop: '24px' }}>
                <button 
                  className="btn btn-secondary" 
                  onClick={handlePrevStep}
                  disabled={verifying}
                >
                  ← Previous
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSurveyComplete}
                  style={{ marginLeft: '12px' }}
                  disabled={verifying}
                >
                  {verifying ? '🔄 Checking submission...' : 'I\'ve Completed the Form - Continue'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Order Opportunities */}
        {currentStep === 5 && (
          <div className="step-panel step-panel-survey">
            <div className="survey-header-content">
              <h2>Step 5: Order Opportunities</h2>
              <p>Complete this final form to set up your opportunity preferences and get ready to start receiving leads</p>
              <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                This is the final step to complete your onboarding. Once submitted, you'll have full access to your dashboard and can start managing your opportunities.
              </p>

              {/* ⭐ Same GHL verification error UI as Step 4 */}
              {verificationError && showVerificationError && (
                <div className="alert alert-danger" style={{ 
                  backgroundColor: '#b96363', 
                  border: '1px solid #fcc', 
                  borderRadius: '8px', 
                  padding: '16px', 
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  position: 'relative'
                }}>
                  <span style={{ fontSize: '24px', flexShrink: 0 }}>⚠️</span>
                  <div style={{ flex: 1 }}>
                    <strong>Order Form Not Completed</strong>
                    <p style={{ margin: '4px 0 0 0' }}>{verificationError}</p>
                    <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                      Please complete the order form above using your registered email (<strong>{profile?.email}</strong>) before clicking the button.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowVerificationError(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#fff',
                      fontSize: '24px',
                      cursor: 'pointer',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      flexShrink: 0,
                      lineHeight: '1'
                    }}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
            
            <div className="survey-container">
              <iframe
                src={(() => {
                  // Build form URL with pre-filled data
                  const baseUrl = 'https://hello.fluxprotocol.ai/widget/form/Kx9uqAz9JC2hSZ8LVzrN';
                  const params = new URLSearchParams();
                  
                  // Add basic profile fields — use member-scoped identity for group members
                  const { email: effEmail2 } = getEffectiveAgentInfo(profile);
                  const effMember2 = profile?._isMemberLogin
                    ? (profile.groupMembers || []).find(m => m.email === profile._loggedInMemberEmail)
                    : null;
                  const effFirst2 = effMember2?.firstName || effMember2?.name?.split(' ')[0] || profile?.firstName;
                  const effLast2  = effMember2?.lastName  || effMember2?.name?.split(' ').slice(1).join(' ') || profile?.lastName;
                  const effPhone2 = effMember2?.phone || profile?.phone;
                  if (effFirst2) params.append('first_name', effFirst2);
                  if (effLast2)  params.append('last_name',  effLast2);
                  if (effEmail2) params.append('email', effEmail2);
                  if (effPhone2) params.append('phone', effPhone2);
                  
                  const queryString = params.toString();
                  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
                })()}
                title="Order Opportunities Form"
                width="100%"
                height="700px"
                style={{ border: 'none', borderRadius: '8px' }}
                allow="fullscreen"
              />
            </div>

            <div className="survey-footer-content">
              <div className="step-actions" style={{ marginTop: '24px' }}>
              <button 
                className="btn btn-secondary" 
                onClick={handlePrevStep}
              >
                ← Previous
              </button>
              {/* ⭐ Use same GHL verification (handleSurveyComplete) as step 4 — blocks access without order form submission */}
              <button 
                className="btn btn-primary"
                onClick={handleSurveyComplete}
                style={{ marginLeft: '12px' }}
                disabled={verifying}
              >
                {verifying ? '🔄 Checking submission...' : "I've Completed the Form - Continue →"}
              </button>
              </div>

              {/* {surveyCompleted && (
                <div className="completion-panel" style={{ 
                  marginTop: '24px',
                  backgroundColor: '#d4edda',
                  border: '1px solid #c3e6cb',
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'center'
                }}>
                  <h3 style={{ color: '#155724', marginBottom: '12px' }}>🎉 Onboarding Complete!</h3>
                  <p style={{ color: '#155724', marginBottom: '20px' }}>
                    All set! You're ready to start accessing opportunities on your dashboard.
                  </p>
                  <button 
                    className="btn btn-success btn-lg"
                    onClick={handleFinishOnboarding}
                  >
                    Go to Dashboard →
                  </button>
                </div>
              )} */}

              <div style={{ 
                marginTop: '16px',
                backgroundColor: '#e7f3ff',
                border: '1px solid #b3d9ff',
                borderRadius: '6px',
                padding: '12px 16px',
                fontSize: '13px',
                color: '#0066cc'
              }}>
                ℹ️ <strong>Ready?</strong> After completing this form, you'll be redirected to your dashboard where you can start managing leads and opportunities.
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Book a Call — small footer link */}
      <div style={{ textAlign: 'center', padding: '12px 0 4px', marginTop: '8px' }}>
        <span style={{ fontSize: '12px', color: '#999' }}>
          Stuck?{' '}
          <button
            onClick={() => window.open('https://hello.fluxprotocol.ai/widget/booking/wRaLks1KHeWjbxKlG0h5')}
            style={{
              background: 'none', border: 'none', color: '#0066cc',
              cursor: 'pointer', fontSize: '12px', textDecoration: 'underline', padding: 0
            }}
          >
            Book a call with an account manager
          </button>
        </span>
      </div>
    </div>
  );
}

export default AgentOnboarding;
